import { resolveTypeConfig } from './applicationQuestionTypeConfig';
import { dataGridRowCount } from './dataGridQuestionOptions';

export function isDataGridQuestionType(question) {
  const type = String(question?.type || '').toUpperCase();
  return type === 'DATA_GRID' || type === 'COMPARISON_TABLE' || type === 'DROPDOWN_GRID' || type === 'DYNAMIC_TABLE';
}

export function dataGridColumns(question, value = null) {
  const cfg = resolveTypeConfig(question);
  if (Array.isArray(cfg.columns) && cfg.columns.length) {
    return cfg.columns.map((c, i) => {
      const type = String(c?.type || 'text').toLowerCase();
      let options = [];
      if (Array.isArray(c?.options)) {
        options = c.options.map((o) => (typeof o === 'object' ? String(o.label || o.value || '') : String(o || ''))).filter(Boolean);
      } else if (typeof c?.options === 'string') {
        options = c.options.split('\n').map((s) => s.trim()).filter(Boolean);
      }
      return {
        key: String(c.key || `col${i}`),
        label: String(c.label || c.key || `Column ${i + 1}`),
        type,
        options,
        uniqueAcrossRows: Boolean(c.uniqueAcrossRows ?? c.uniqueSelection ?? c.unique),
      };
    });
  }
  const rows = Array.isArray(value) ? value : [];
  const keys = new Set();
  for (const row of rows) {
    if (row && typeof row === 'object' && !Array.isArray(row)) {
      Object.keys(row).forEach((k) => keys.add(k));
    }
  }
  if (keys.size) {
    return [...keys].map((key) => ({ key, label: key }));
  }
  return [
    { key: 'col1', label: 'Column 1' },
    { key: 'col2', label: 'Column 2' },
    { key: 'col3', label: 'Column 3' },
  ];
}

export function normalizeDataGridRows(value, question) {
  const cols = dataGridColumns(question, value);
  const baseCount = dataGridRowCount(question);
  const rows = Array.isArray(value) ? value : [];
  const count = Math.max(baseCount, rows.length);
  const out = [];
  for (let i = 0; i < count; i += 1) {
    const row = rows[i] && typeof rows[i] === 'object' && !Array.isArray(rows[i]) ? rows[i] : {};
    out.push(Object.fromEntries(
      cols.map((c) => [c.key, row[c.key] != null ? String(row[c.key]) : '']),
    ));
  }
  return out;
}

/** Coerce stored answer shapes (legacy TABLE_ROWS, JSON strings, single-row objects) into grid rows. */
export function coerceDataGridAnswerValue(value, question) {
  if (value == null || value === '') {
    return normalizeDataGridRows([], question);
  }

  let raw = value;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return normalizeDataGridRows([], question);
    try {
      raw = JSON.parse(trimmed);
    } catch {
      return normalizeDataGridRows([], question);
    }
  }

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const keys = Object.keys(raw);
    const numericKeysOnly = keys.length > 0 && keys.every((k) => /^\d+$/.test(k));
    if (numericKeysOnly) {
      const rows = keys
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => raw[k]);
      return normalizeDataGridRows(rows, question);
    }
    const cols = dataGridColumns(question, raw);
    const hasKnownColumn = cols.some((c) => raw[c.key] != null && String(raw[c.key]).trim() !== '');
    const hasAnyCell = keys.some((k) => raw[k] != null && String(raw[k]).trim() !== '');
    if (hasKnownColumn || hasAnyCell) {
      return normalizeDataGridRows([raw], question);
    }
  }

  if (Array.isArray(raw)) {
    if (raw.length && raw.every((v) => v == null || typeof v === 'string' || typeof v === 'number')) {
      const cols = dataGridColumns(question);
      const row = Object.fromEntries(cols.map((c, i) => [c.key, raw[i] != null ? String(raw[i]) : '']));
      return normalizeDataGridRows([row], question);
    }
    return normalizeDataGridRows(raw, question);
  }

  return normalizeDataGridRows([], question);
}

export function isDataGridAnswerEmpty(value, question) {
  const rows = coerceDataGridAnswerValue(value, question);
  return !rows.some((row) => row && typeof row === 'object'
    && Object.values(row).some((cell) => String(cell ?? '').trim()));
}

export function formatDataGridAnswerText(value, question) {
  const cols = dataGridColumns(question, value);
  const rows = coerceDataGridAnswerValue(value, question);
  if (!cols.length) return '';
  const lines = rows.map((row, ri) => {
    const cells = cols.map((c) => {
      const cell = row[c.key];
      const shown = cell != null && String(cell).trim() ? String(cell).trim() : '—';
      return `${c.label}: ${shown}`;
    });
    return rows.length > 1 ? `Row ${ri + 1}: ${cells.join(' | ')}` : cells.join(' | ');
  });
  return lines.filter(Boolean).join('\n');
}

/** Merge stored answer metadata with the live form question definition when available. */
export function resolveQuestionForStoredAnswer(answer, questionById) {
  if (!answer) return null;
  const fromForm = questionById?.get?.(String(answer.questionId || ''));
  if (fromForm) return fromForm;
  return {
    id: answer.questionId,
    type: answer.questionType,
    label: answer.questionLabel,
    options: answer.questionOptions,
  };
}

export function buildApplicationFormQuestionMap(form) {
  const map = new Map();
  for (const q of form?.questions || []) {
    if (q?.id != null) map.set(String(q.id), q);
  }
  for (const cqs of form?.categoryQuestionSets || []) {
    for (const q of cqs?.questions || []) {
      if (q?.id != null) map.set(String(q.id), q);
    }
  }
  for (const cat of form?.categories || []) {
    for (const q of cat?.questions || []) {
      if (q?.id != null) map.set(String(q.id), q);
    }
  }
  return map;
}
